/**
 * @file Floating status markers — the "!" an enemy shows when it spots you.
 *
 * Aggro is invisible without it: the player has no way to tell a patrolling
 * enemy from one that has already committed to hunting them. This system turns
 * the {@link ../core/EventBus.js Events.ENEMY_AGGRO} edge into a billboard over
 * the agent's head that fades out on its own.
 *
 * The glyph is drawn once into a canvas and shared as a texture by every
 * sprite, and the sprites themselves come from a pool, so a wave of enemies all
 * noticing the player at once costs no allocations and no texture uploads.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import { Transform, Renderable, VisualIndicator, IndicatorKind } from '../components/index.js';
import { Events } from '../core/EventBus.js';
import { PALETTE } from '../config/Palette.js';

/** Seconds a marker stays up. */
const DURATION = 0.8;
/** Trailing seconds spent fading to nothing. */
const FADE_TIME = 0.25;
/** Leading seconds spent popping up to full size. */
const POP_TIME = 0.1;
/** World-space height of the billboard. */
const SIZE = 0.85;
/** Gap between the top of the body and the bottom of the marker. */
const HEAD_GAP = 0.45;
/** Fallback height for entities without a mesh to measure. */
const DEFAULT_HEIGHT = 1.5;
/** Sprites created up front; the pool grows if a wave ever outruns it. */
const POOL_SIZE = 24;
/** Texture resolution of the glyph. */
const TEXTURE_SIZE = 64;
/** Drawn after the rest of the transparent pass so markers sit on top. */
const RENDER_ORDER = 20;

export class VisualIndicatorSystem extends System {
  static priority = 80;

  constructor() {
    super();
    /** @type {Map<string, THREE.Texture | null>} One glyph texture per kind. @private */
    this._textures = new Map();
    /** @type {THREE.Sprite[]} Idle billboards ready to be handed out. @private */
    this._pool = [];
    /** @type {THREE.Sprite[]} Every sprite ever created, for teardown. @private */
    this._sprites = [];
    /**
     * Live markers. Keyed by entity because components must not hold Three.js
     * objects — see {@link ../components/Renderable.js}.
     * @type {Map<import('../ecs/Entity.js').Entity, THREE.Sprite>}
     * @private
     */
    this._active = new Map();
    /** @type {(() => void)[]} @private */
    this._subscriptions = [];
  }

  /** @returns {void} */
  init() {
    for (let i = 0; i < POOL_SIZE; i++) this._pool.push(this._createSprite());

    this._subscriptions.push(
      this.ctx.events.on(Events.ENEMY_AGGRO, ({ entity }) => this._alert(entity)),
      this.ctx.events.on(Events.ENTITY_DESTROYED, (entity) => this._release(entity))
    );
  }

  /**
   * Raises (or replays) the alert marker over an entity.
   * @param {import('../ecs/Entity.js').Entity | null} entity Entity that just aggroed.
   * @returns {void}
   * @private
   */
  _alert(entity) {
    if (!entity || !entity.alive || !entity.has(Transform)) return;

    const existing = entity.get(VisualIndicator);
    if (existing) {
      // Same head, same billboard: a re-trigger replays it instead of stacking.
      existing.restart();
      const sprite = this._active.get(entity);
      if (sprite) this._place(sprite, entity.get(Transform), existing, 0);
      return;
    }

    const indicator = new VisualIndicator({
      kind: IndicatorKind.ALERT,
      duration: DURATION,
      yOffset: this._headHeight(entity) + HEAD_GAP,
      scale: SIZE
    });
    entity.add(indicator);

    const sprite = this._acquire(IndicatorKind.ALERT);
    sprite.visible = true;
    this._active.set(entity, sprite);
    this.ctx.scene.add(sprite);
    // Place immediately: ENEMY_AGGRO often fires mid-frame (damage), and a
    // lethal hit marks the entity dead before this system's update runs — the
    // query then skips it and the sprite would flash at the world origin.
    this._place(sprite, entity.get(Transform), indicator, 0);
  }

  /**
   * @param {number} dt Delta time in seconds.
   * @returns {void}
   */
  update(dt) {
    for (const entity of this.world.query(VisualIndicator, Transform)) {
      const indicator = entity.get(VisualIndicator);
      indicator.remaining -= dt;

      if (indicator.remaining <= 0) {
        this._release(entity);
        continue;
      }

      const sprite = this._active.get(entity);
      if (!sprite) continue;

      // Snap up to size, hold, then fade — the pop is what makes it read as an
      // event rather than as part of the enemy's silhouette.
      const elapsed = indicator.duration - indicator.remaining;
      this._place(sprite, entity.get(Transform), indicator, elapsed);
    }
  }

  /**
   * Writes world pose + pop/fade into a live billboard.
   * @param {THREE.Sprite} sprite Billboard to update.
   * @param {import('../components/Transform.js').Transform} transform Entity pose.
   * @param {import('../components/VisualIndicator.js').VisualIndicator} indicator Marker data.
   * @param {number} elapsed Seconds since the marker started (0 on raise/restart).
   * @returns {void}
   * @private
   */
  _place(sprite, transform, indicator, elapsed) {
    const { position } = transform;
    sprite.position.set(position.x, position.y + indicator.yOffset, position.z);

    const pop = POP_TIME > 0 ? Math.min(1, elapsed / POP_TIME) : 1;
    const scale = indicator.scale * (0.55 + 0.45 * pop * (2 - pop));
    sprite.scale.set(scale, scale, scale);
    sprite.material.opacity = Math.min(1, indicator.remaining / FADE_TIME);
  }

  /**
   * Drops an entity's marker, returning the sprite to the pool. Safe for
   * entities that never had one, which is what lets it double as the
   * entity-destroyed handler.
   * @param {import('../ecs/Entity.js').Entity} entity Entity losing its marker.
   * @returns {void}
   * @private
   */
  _release(entity) {
    const sprite = this._active.get(entity);
    if (sprite) {
      this._active.delete(entity);
      sprite.removeFromParent();
      sprite.visible = false;
      this._pool.push(sprite);
    }
    // A destroyed entity has already had its components wiped by the world.
    if (entity.alive) entity.remove(VisualIndicator);
  }

  /**
   * Top of an entity's body, derived from the render offset so every archetype
   * gets its own headroom without this system knowing the enemy table.
   * @param {import('../ecs/Entity.js').Entity} entity Entity to measure.
   * @returns {number} Height above the transform origin.
   * @private
   */
  _headHeight(entity) {
    const renderable = entity.get(Renderable);
    return renderable && renderable.yOffset > 0 ? renderable.yOffset * 2 : DEFAULT_HEIGHT;
  }

  /**
   * @param {string} kind One of {@link IndicatorKind}.
   * @returns {THREE.Sprite} A billboard ready to be positioned.
   * @private
   */
  _acquire(kind) {
    const sprite = this._pool.pop() ?? this._createSprite();
    sprite.material.map = this._texture(kind);
    sprite.material.needsUpdate = true;
    return sprite;
  }

  /**
   * @returns {THREE.Sprite} A fresh, hidden billboard with its own material.
   * @private
   */
  _createSprite() {
    // Opacity is per-material, so markers cannot share one: each fades alone.
    const material = new THREE.SpriteMaterial({
      map: this._texture(IndicatorKind.ALERT),
      transparent: true,
      // Depth-test off so dungeon walls/props cannot hide the "!" (issue #17 AC).
      depthTest: false,
      depthWrite: false,
      toneMapped: false
    });

    const sprite = new THREE.Sprite(material);
    sprite.name = 'Indicator';
    sprite.renderOrder = RENDER_ORDER;
    sprite.visible = false;
    this._sprites.push(sprite);
    return sprite;
  }

  /**
   * @param {string} kind One of {@link IndicatorKind}.
   * @returns {THREE.Texture | null} The shared glyph, drawn on first use.
   * @private
   */
  _texture(kind) {
    if (this._textures.has(kind)) return this._textures.get(kind);
    const texture = this._drawGlyph('!', PALETTE.alert);
    this._textures.set(kind, texture);
    return texture;
  }

  /**
   * Rasterises a glyph into a texture. Canvas text keeps the marker free of any
   * font asset or `TextGeometry` — one 64px texture for the whole game.
   * @param {string} glyph Character to draw.
   * @param {number} color Hex colour.
   * @returns {THREE.Texture | null} Null when there is no DOM (headless tests).
   * @private
   */
  _drawGlyph(glyph, color) {
    if (typeof document === 'undefined') return null;

    const canvas = document.createElement('canvas');
    canvas.width = TEXTURE_SIZE;
    canvas.height = TEXTURE_SIZE;

    const context = canvas.getContext('2d');
    const center = TEXTURE_SIZE / 2;
    const hex = `#${color.toString(16).padStart(6, '0')}`;

    context.font = `bold ${TEXTURE_SIZE * 0.78}px monospace`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    // Dark outline first: the neon palette leaves the marker unreadable against
    // a bright enemy body otherwise.
    context.lineWidth = TEXTURE_SIZE * 0.11;
    context.strokeStyle = '#120309';
    context.strokeText(glyph, center, center);
    context.fillStyle = hex;
    context.fillText(glyph, center, center);

    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  }

  /** @returns {void} */
  dispose() {
    for (const unsubscribe of this._subscriptions) unsubscribe();
    this._subscriptions.length = 0;

    for (const sprite of this._sprites) {
      sprite.removeFromParent();
      sprite.material.dispose();
    }
    this._sprites.length = 0;
    this._pool.length = 0;
    this._active.clear();

    for (const texture of this._textures.values()) texture?.dispose();
    this._textures.clear();
  }
}
